import {useState} from 'react'
import {Link, useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {HelpIcon} from '@/components/help-icon'
import {KairosMark} from '@/components/kairos-mark'
import {SectionCard} from '@/components/section-card'
import {EstadoLinea} from '@/components/incidentes/estado-linea'
import {EliminarIncidenteDialog} from '@/components/incidentes/eliminar-incidente-dialog'
import {FichaTab} from '@/components/incidentes/ficha-tab'
import {Checklist, ReabrirBox} from '@/components/incidente-checklist'
import {
  useIncidente,
  usePatchIncidente,
  type Incidente,
  type IncidenteOrigen,
  type IncidenteReloj,
} from '@/hooks/sgsst/incidentes/use-incidentes'
import {useBusiness, useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {translateApiError} from '@/lib/errors'
import {formatShortDate} from '@/lib/risk'
import {ChevronGlyph} from '@/components/ui/icons'
import {cn} from '@/lib/utils'

// `dias_restantes` es null con el reloj cumplido; la intersección tolera su ausencia en payloads viejos cacheados.
type RelojConDias = IncidenteReloj & {dias_restantes?: number | null}

/** Nunca deriva días: `dias_restantes` viene calculado con el reloj del backend (viajable en pruebas);
 * sin el campo o con el reloj cumplido degrada a verde. */
function relojTone(reloj: RelojConDias): 'green' | 'amber' | 'red' {
  if (reloj.vencido) return 'red'
  const dias = reloj.dias_restantes
  if (dias == null) return 'green'
  if (dias <= 1) return 'red'
  if (dias <= 7) return 'amber'
  return 'green'
}

const RELOJ_RAIL: Record<'green' | 'amber' | 'red', string> = {
  green: 'border-l-tone-green-fg',
  amber: 'border-l-tone-amber-fg',
  red: 'border-l-tone-red-fg',
}

/** Una sola marca con tono a propósito: la aserción contra el reloj viajado busca «el span con tono». */
function RelojChip({reloj}: {reloj: RelojConDias}) {
  const {t} = useTranslation()
  const [showBase, setShowBase] = useState(false)
  const cumplido = reloj.cumplido_at !== null
  const tone = cumplido ? 'green' : relojTone(reloj)
  const texto = cumplido
    ? t('incidentes.reloj.cumplido')
    : reloj.vencido
      ? t('incidentes.reloj.vencidoDesde', {fecha: formatShortDate(reloj.deadline)})
      : reloj.dias_restantes != null
        ? t('incidentes.reloj.quedan', {dias: reloj.dias_restantes})
        : t('incidentes.reloj.vence', {fecha: formatShortDate(reloj.deadline)})
  return (
    <button
      type="button"
      data-testid={`reloj-${reloj.clave}`}
      aria-expanded={showBase}
      onClick={() => setShowBase((v) => !v)}
      className={cn(
        'w-full rounded-lg border border-l-[3px] border-border p-3 text-left transition-colors hover:bg-secondary/40',
        RELOJ_RAIL[tone]
      )}>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <span className="min-w-0 text-sm font-medium">{reloj.label}</span>
        <span className="flex shrink-0 items-center gap-1.5">
          <Tag tone={tone} className="text-[11px]">
            {texto}
          </Tag>
          <ChevronGlyph className={cn('text-muted-foreground', showBase && 'rotate-180')} />
          <span className="sr-only">{t('common.verBaseLegal')}</span>
        </span>
      </div>
      <p className="mt-1 font-mono text-[11px] text-muted-foreground tabular-nums">
        {t('incidentes.reloj.vence', {fecha: formatShortDate(reloj.deadline)})}
      </p>
      {showBase && <p className="mt-1 text-xs text-muted-foreground">{reloj.base_legal}</p>}
    </button>
  )
}

/** Relojes activos, el peor primero; los del art. 14 (ARL, Ministerio) arrancan al sellar el informe. */
function BannerTiempos({incidente}: {incidente: Incidente}) {
  const {t} = useTranslation()
  // `deadline` es ISO (YYYY-MM-DD): el orden lexicográfico es el cronológico.
  const relojes = [...incidente.relojes].sort(
    (a, b) => Number(b.vencido) - Number(a.vencido) || a.deadline.localeCompare(b.deadline)
  )
  return (
    <SectionCard testId="incidente-banner" title={t('incidentes.banner.eyebrow')} bodyClassName="space-y-2 p-5">
      {/* O8.1: evento ya gestionado con la ARL por fuera: relojes muertos, sin alertas falsas. */}
      {incidente.reportado_arl_externamente ? (
        <div className="tone-green rounded-lg px-3 py-2 text-sm" data-testid="incidente-arl-externo">
          <p className="font-medium">{t('incidentes.banner.reportadoArlExterno')}</p>
          {incidente.radicado_externo && (
            <p className="mt-0.5 font-mono text-xs">
              {t('incidentes.banner.radicadoExterno', {radicado: incidente.radicado_externo})}
            </p>
          )}
        </div>
      ) : (
        <>
          {relojes.map((reloj) => (
            <RelojChip key={reloj.clave} reloj={reloj} />
          ))}
          {relojes.length === 0 && <p className="text-sm text-muted-foreground">{t('incidentes.banner.vacio')}</p>}
        </>
      )}
    </SectionCard>
  )
}

/** Exposición a multas al vencer (§2.3 D): orientativa, no asesoría legal. */
function ExposicionCard({incidente}: {incidente: Incidente}) {
  const {t} = useTranslation()
  if (incidente.exposiciones.length === 0) return null
  const fmt = (n: number) => `$${Math.round(n).toLocaleString('es-CO')}`
  return (
    <SectionCard testId="incidente-exposicion" title={t('incidentes.exposicion.eyebrow')} bodyClassName="space-y-3 p-5">
      {incidente.exposiciones.map((exp) => (
        <div key={exp.clase} className="tone-red rounded-lg p-3">
          <p className="text-sm">{t(`incidentes.exposicion.mensaje_${exp.clase}`, {max: fmt(exp.cop_high)})}</p>
          <p className="mt-1 font-mono text-[10px] tracking-wider uppercase opacity-80">{exp.base_legal}</p>
        </div>
      ))}
      <p className="text-xs text-muted-foreground">{t('incidentes.exposicion.disclaimer')}</p>
    </SectionCard>
  )
}

/** Origen y días de incapacidad llegan semanas después del evento (§3.1/§5 regla 1); un PATCH por campo. */
function DatosTardiosForm({incidente, businessId}: {incidente: Incidente; businessId: string}) {
  const {t} = useTranslation()
  const patch = usePatchIncidente(businessId, incidente.id)
  const [origen, setOrigen] = useState<IncidenteOrigen>(incidente.origen)
  const [dias, setDias] = useState(incidente.dias_incapacidad?.toString() ?? '')
  const [error, setError] = useState<string | null>(null)
  const ORIGENES: IncidenteOrigen[] = ['pendiente_calificacion', 'accidente_trabajo', 'enfermedad_laboral', 'comun']
  const campoCls =
    'h-10 w-full rounded-lg border border-input bg-card px-3 text-sm transition-colors outline-none hover:border-[color-mix(in_oklch,var(--input),var(--foreground)_18%)] focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50'

  function save() {
    setError(null)
    patch.mutate(
      {origen, dias_incapacidad: dias.trim() ? Number(dias) : null},
      {onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <SectionCard
      testId="incidente-datos-tardios"
      title={t('incidentes.tardios.eyebrow')}
      actions={
        <HelpIcon
          label={t('common.help.iconLabelFor', {campo: t('incidentes.tardios.eyebrow')})}
          testId="incidente-tardios-tip">
          {t('incidentes.tardios.tip')}
        </HelpIcon>
      }
      bodyClassName="space-y-3 p-5">
      <label className="block space-y-1.5">
        <span className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
          {t('incidentes.tardios.origenLabel')}
        </span>
        <select
          value={origen}
          data-testid="incidente-origen"
          onChange={(e) => setOrigen(e.target.value as IncidenteOrigen)}
          className={campoCls}>
          {ORIGENES.map((o) => (
            <option key={o} value={o}>
              {t(`incidentes.origen.${o}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="block space-y-1.5">
        <span className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
          {t('incidentes.tardios.diasLabel')}
        </span>
        <input
          type="number"
          min={0}
          value={dias}
          data-testid="incidente-dias-incapacidad"
          onChange={(e) => setDias(e.target.value)}
          className={cn(campoCls, 'font-mono tabular-nums')}
        />
      </label>
      <Button size="sm" data-testid="incidente-tardios-guardar" disabled={patch.isPending} onClick={save}>
        {t('incidentes.tardios.guardar')}
      </Button>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </SectionCard>
  )
}

/** `readOnly` ya incluye cerrado; `puedeReabrir` es la única puerta abierta entonces, y solo para operador. */
function GestionTab({
  incidente,
  businessId,
  readOnly,
  cerrado,
  puedeReabrir,
  canOperate,
}: {
  incidente: Incidente
  businessId: string
  readOnly: boolean
  cerrado: boolean
  puedeReabrir: boolean
  // Explícito y no derivado de `readOnly`: el checklist pide la investigación del evento
  // para colgar ahí los comprobantes, y esa consulta es operador-only en el backend.
  canOperate: boolean
}) {
  return (
    <div className="space-y-5">
      <BannerTiempos incidente={incidente} />
      <ExposicionCard incidente={incidente} />
      {!readOnly && <Checklist incidente={incidente} canOperate={canOperate} />}
      {!readOnly && <DatosTardiosForm incidente={incidente} businessId={businessId} />}
      {cerrado && puedeReabrir && <ReabrirBox incidente={incidente} businessId={businessId} />}
    </div>
  )
}

type TabKey = 'ficha' | 'gestion'
function parseTab(value: string | null): TabKey | null {
  return value === 'ficha' || value === 'gestion' ? value : null
}

export function IncidenteDetallePage() {
  const {t} = useTranslation()
  const {businessId, incidenteId} = useParams()
  const {data: incidente, isLoading} = useIncidente(businessId, incidenteId)
  const {data: business} = useBusiness(businessId)
  const capabilities = useCapabilities(businessId)
  const [searchParams, setSearchParams] = useSearchParams()
  const godReadOnly = useGodModeReadOnly()
  const navigate = useNavigate()
  const [eliminando, setEliminando] = useState(false)

  const tab = parseTab(searchParams.get('tab')) ?? 'ficha'
  const esNuevo = searchParams.get('nuevo') === '1'
  function setTab(next: TabKey) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      {replace: true}
    )
  }

  if (isLoading || !incidente) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  const cerrado = incidente.estado === 'cerrado'
  const puedeReabrir = business?.role === 'owner' || business?.role === 'responsable'
  const readOnly = godReadOnly || cerrado
  const canOperate = capabilities.sgsst_operar

  return (
    <div className="space-y-5" data-testid="incidente-detalle">
      <div>
        <Link
          to={`/businesses/${businessId}/incidentes`}
          className="inline-flex min-h-8 items-center text-[13px] font-medium text-muted-foreground hover:text-foreground">
          {t('incidentes.detalle.volver')}
        </Link>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <KairosMark className="text-primary" />
            <p className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
              {t(`incidentes.tipo.${incidente.tipo}`)} · {t(`incidentes.gravedad.${incidente.gravedad_reportada}`)}
            </p>
          </div>
          {canOperate && !godReadOnly && (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="text-destructive"
                data-testid="incidente-eliminar"
                onClick={() => setEliminando(true)}>
                {t('incidentes.eliminar.action')}
              </Button>
              <EliminarIncidenteDialog
                businessId={businessId!}
                incidente={incidente}
                open={eliminando}
                onOpenChange={setEliminando}
                onDeleted={() => navigate(`/businesses/${businessId}/incidentes`)}
              />
            </>
          )}
        </div>
        <h1 className="mt-1 font-heading text-2xl">{incidente.descripcion_hechos}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          {cerrado && (
            <span className="text-xs text-muted-foreground" data-testid="incidente-solo-lectura">
              {t('incidentes.detalle.soloLectura')}
            </span>
          )}
          {incidente.afectado_nombre &&
            (incidente.afectado_employee_id !== null && canOperate ? (
              // Abre la ficha de la persona en «Eventos», donde vive este mismo expediente del otro lado.
              <Link
                to={`/businesses/${businessId}/empleados/${incidente.afectado_employee_id}?tab=eventos`}
                data-testid="incidente-afectado-perfil"
                className="text-sm font-medium text-primary hover:underline">
                {t('incidentes.detalle.afectado', {nombre: incidente.afectado_nombre})}
              </Link>
            ) : (
              <span className="text-sm text-muted-foreground">
                {t('incidentes.detalle.afectado', {nombre: incidente.afectado_nombre})}
              </span>
            ))}
          <span className="font-mono text-xs text-muted-foreground tabular-nums">
            {formatShortDate(incidente.evento_fecha)}
          </span>
        </div>
      </div>

      <div className="surface px-4 py-3">
        <EstadoLinea estado={incidente.estado} furatSellado={incidente.furat_reported_at !== null} />
      </div>

      {esNuevo && (
        <div
          className="tone-green flex flex-wrap items-center justify-between gap-2 rounded-xl px-4 py-3"
          data-testid="incidente-nuevo">
          <p className="text-sm font-medium">{t('incidentes.detalle.nuevoTitulo')}</p>
          <Button size="sm" variant="outline" data-testid="incidente-nuevo-cta" onClick={() => setTab('gestion')}>
            {t('incidentes.detalle.nuevoCta')}
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="ficha" data-testid="tab-ficha">
            {t('incidentes.tabs.ficha')}
          </TabsTrigger>
          <TabsTrigger value="gestion" data-testid="tab-gestion">
            {t('incidentes.tabs.gestion')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ficha" className="pt-4">
          <FichaTab incidente={incidente} businessId={businessId!} readOnly={readOnly} canOperate={canOperate} />
        </TabsContent>

        <TabsContent value="gestion" className="pt-4">
          <GestionTab
            incidente={incidente}
            businessId={businessId!}
            readOnly={readOnly}
            cerrado={cerrado}
            puedeReabrir={puedeReabrir && !godReadOnly}
            canOperate={canOperate}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
